import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useLocation } from 'react-router-dom'
import { parse } from 'query-string'
import styled from 'styled-components'

import { SearchBar } from '@centivo/provider-directory-components'

import { decodeResultsSearch } from './functions/resultsSearchParams'
import { useDirectorySearchBar } from './functions/useDirectorySearchBar'

// Rendered as a sibling above the padded results container, so the beige band
// spans the full page width on its own.
const SearchBarSlot = styled.div`
  margin-bottom: 0.5rem;
  background-color: #f7f4e6;
`

// Constrains only the search controls to the page max-width and centers them,
// while the beige band above keeps filling the full page width. The horizontal
// padding matches the results page so the fields align with the results below.
const SearchBarInner = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 2rem 2.5rem;
`

interface ResultsSearchBarFieldProps {
  search: string
  onNavigate: (search: string) => void
}

/**
 * The actual search bar. It seeds its initial values from the URL once, on
 * mount. The parent controls when this remounts (and therefore reseeds) via a
 * `key`, so a search fired from here does not tear it down and rebuild it.
 */
const ResultsSearchBarField: React.FC<ResultsSearchBarFieldProps> = ({
  search,
  onNavigate,
}) => {
  const initial = useMemo(
    () =>
      decodeResultsSearch(
        parse(search) as Record<string, string | string[] | null>
      ),
    [search]
  )

  const { searchBar, handleSubmit } = useDirectorySearchBar(
    {
      query:
        initial?.selectedServiceLine?.label ??
        initial?.selectedCondition?.label ??
        initial?.query ??
        '',
      zipCode: initial?.zipCode ?? '',
      placeId: initial?.placeId,
    },
    onNavigate
  )

  useEffect(() => {
    if (
      initial?.selectedSmartSuggestion &&
      'query' in initial.selectedSmartSuggestion
    ) {
      searchBar.onQuerySuggestionSelect({
        kind: 'smartSuggestion',
        item: initial.selectedSmartSuggestion,
      })
    } else if (initial?.selectedServiceLine) {
      searchBar.onQuerySuggestionSelect({
        kind: 'serviceLine',
        item: initial.selectedServiceLine,
      })
    } else if (initial?.selectedCondition) {
      searchBar.onQuerySuggestionSelect({
        kind: 'condition',
        item: initial.selectedCondition,
      })
    }
    // Run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * The library seeds the query text on mount and immediately runs the
   * service-line/condition autocomplete for it — there is no "skip the first
   * fetch" guard for the query field (unlike the zip field) — which pops the
   * suggestions dropdown open on arrival. Keep the query suggestions closed
   * until the user actually edits the query field, so a seeded search never
   * auto-opens the dropdown.
   */
  const [hasEditedQuery, setHasEditedQuery] = useState(false)

  const onQueryChange = useCallback(
    (value: string) => {
      setHasEditedQuery(true)
      searchBar.onQueryChange(value)
    },
    [searchBar]
  )

  return (
    <SearchBar
      {...searchBar}
      onQueryChange={onQueryChange}
      isQuerySuggestionsOpen={
        hasEditedQuery && searchBar.isQuerySuggestionsOpen
      }
      onSubmit={handleSubmit}
    />
  )
}

/**
 * The provider search bar shown at the top of the results page. It seeds from
 * the URL and, on submit, navigates back to `/results` with the new search
 * encoded in the URL — which the page reads to fetch results.
 *
 * The field reseeds from the URL only when the URL changes for a reason other
 * than this bar's own submit (initial load, refresh, deep link, or browser
 * back/forward between searches). A search fired from here keeps the same field
 * instance — its state already reflects what the user searched — so it does not
 * remount and reopen the suggestions dropdown.
 */
export const ResultsSearchBar: React.FC = () => {
  const { search } = useLocation()

  const selfNavigatedSearchRef = useRef<string | null>(null)
  const [seedToken, setSeedToken] = useState(0)
  const [lastSeededSearch, setLastSeededSearch] = useState(search)

  if (
    search !== lastSeededSearch &&
    search !== selfNavigatedSearchRef.current
  ) {
    setSeedToken(token => token + 1)
    setLastSeededSearch(search)
  }

  const onNavigate = useCallback((nextSearch: string) => {
    selfNavigatedSearchRef.current = nextSearch
  }, [])

  return (
    <SearchBarSlot>
      <SearchBarInner>
        <ResultsSearchBarField
          key={seedToken}
          search={search}
          onNavigate={onNavigate}
        />
      </SearchBarInner>
    </SearchBarSlot>
  )
}
